import { Component, computed, inject, Input, signal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { SAMPLE_SINGLE_DAY } from '../energy-balance-data';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DailyBalanceChartComponent } from './daily-balance-chart.component';
import { MatButtonModule } from '@angular/material/button';

import { environment } from '../../../../../environments/environment';
import { EnergyBalanceResponse } from '../../../../../api/models/energy-balance-response';
import { Api } from '../../../../../api/api';
import { getEnergyBalances } from '../../../../../api/functions';
import { LocationStateService } from '../../../../../common/services/location-state.service';
import {catchError, from, of} from 'rxjs';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'app-daily-energy-balance',
  templateUrl: './daily-balance.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatIconModule, MatInputModule, MatFormFieldModule, MatDatepickerModule, MatButtonModule, DailyBalanceChartComponent],
})
export class DailyBalanceComponent {
  api = inject(Api);
  dateSignal = signal(new Date());
  locationStateService = inject(LocationStateService);
  isLoading = signal<boolean>(false);
  @Input()
  set date(value: Date) {
    this.dateSignal.set(value);
  }

  resource = rxResource<EnergyBalanceResponse | EnergyBalanceResponse[], { location_id: string; aggregation: string; at: string }>({
    params: () => ({
      location_id: this.locationStateService.currentLocation()?.id ?? '',
      aggregation: 'day',
      at: this.dateSignal().toISOString(),
    }),
    defaultValue: environment.showDefault ? SAMPLE_SINGLE_DAY : undefined,
    stream: (loaderParams) => {
      this.isLoading.set(true);

      return from(
        this.api.invoke(getEnergyBalances, loaderParams.params) as Promise<
          EnergyBalanceResponse | EnergyBalanceResponse[]
        >
      ).pipe(
        catchError((error) => {
          if (environment.showDefault) {
            console.error('Service is not reachable. Using sample day balance data.', error);
            return of(SAMPLE_SINGLE_DAY);
          }
          throw error;
        }),
        finalize(() => this.isLoading.set(false)),
      );
    },
  });

  data = computed((): EnergyBalanceResponse => {
    const response = this.resource.value();
    if (!response) {
      return {};
    }
    return Array.isArray(response) ? (response[0] ?? {}) : response;
  });
}
